import { useState } from "react";

const FORM_ADDR = "https://readdy.ai/api/form/da28slbk8r5te5mqsjp0";

interface SubmitState {
  status: "idle" | "loading" | "success" | "error";
  message: string;
}

export default function ContactSection() {
  const [state, setState] = useState<SubmitState>({ status: "idle", message: "" });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("phone_alt")?.toString().trim() ?? "";
    if (honeypot) {
      setState({ status: "success", message: "感谢你的留言，我们会尽快与你联系。" });
      return;
    }
    formData.delete("phone_alt");

    setState({ status: "loading", message: "正在提交..." });
    try {
      const res = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || "";
      if (res.ok && parsed?.code === "OK") {
        setState({ status: "success", message: "提交成功，感谢你的留言与支持！" });
        form.reset();
      } else {
        setState({
          status: "error",
          message: serverMsg || "提交失败，请稍后再试。",
        });
      }
    } catch {
      setState({ status: "error", message: "网络异常，提交失败，请稍后再试。" });
    }
  };

  return (
    <section id="contact" className="bg-accent-50 py-12 md:py-16">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div>
            <p className="inline-flex items-center gap-1.5 rounded-full bg-accent-200 px-3 py-1 text-xs font-medium text-accent-900">
              <i className="ri-customer-service-2-line"></i>
              联系我们
            </p>
            <h2 className="mt-3 font-heading text-2xl text-foreground-950 md:text-3xl">
              有好内容推荐？欢迎告诉我们
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              想看的吃瓜视频没找到，或者有不错的内容想分享给大家？欢迎留言，我们会认真阅读每一条建议，并在后续更新中优先呈现。
            </p>
            <div className="mt-6 flex flex-col gap-3">
              {[
                { icon: "ri-mail-line", text: "feedback@chigua-video.cn" },
                { icon: "ri-time-line", text: "内容每日更新，全年不间断" },
                { icon: "ri-shield-check-line", text: "坚持合规收录，尊重版权" },
              ].map((c) => (
                <div key={c.text} className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-background-50 text-primary-600 ring-1 ring-background-200">
                    <i className={`${c.icon} text-lg`}></i>
                  </span>
                  <span className="text-sm text-foreground-800">{c.text}</span>
                </div>
              ))}
            </div>
          </div>

          <form
            data-readdy-form=""
            id="contact-form"
            onSubmit={handleSubmit}
            className="rounded-lg bg-background-50 p-5 ring-1 ring-background-200 md:p-6"
          >
            <div className="flex flex-col gap-3 sm:flex-row">
              <div className="flex-1">
                <label htmlFor="name" className="mb-1.5 block text-sm text-foreground-800">
                  您的称呼
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  placeholder="怎么称呼您"
                  className="h-11 w-full rounded-md bg-background-100 px-3 text-sm text-foreground-900 outline-none ring-1 ring-background-200 placeholder:text-foreground-400 focus:ring-2 focus:ring-primary-400"
                />
              </div>
              <div className="flex-1">
                <label htmlFor="email" className="mb-1.5 block text-sm text-foreground-800">
                  邮箱地址
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="example@email.com"
                  className="h-11 w-full rounded-md bg-background-100 px-3 text-sm text-foreground-900 outline-none ring-1 ring-background-200 placeholder:text-foreground-400 focus:ring-2 focus:ring-primary-400"
                />
              </div>
            </div>

            <div className="mt-3">
              <label htmlFor="message" className="mb-1.5 block text-sm text-foreground-800">
                留言内容
              </label>
              <textarea
                id="message"
                name="message"
                required
                maxLength={500}
                rows={5}
                placeholder="写下你的建议或想推荐的视频内容（500字以内）..."
                className="w-full resize-none rounded-md bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none ring-1 ring-background-200 placeholder:text-foreground-400 focus:ring-2 focus:ring-primary-400"
              ></textarea>
              <p className="mt-1 text-right text-xs text-foreground-500">最多 500 字</p>
            </div>

            <div className="hp-field" aria-hidden="true">
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                readOnly
              />
            </div>

            <button
              type="submit"
              disabled={state.status === "loading"}
              className="mt-4 inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-6 text-sm font-medium whitespace-nowrap text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
            >
              <i className="ri-send-plane-line"></i>
              {state.status === "loading" ? "提交中..." : "提交留言"}
            </button>

            {state.status !== "idle" && (
              <p
                className={`mt-3 text-sm ${
                  state.status === "success" ? "text-accent-700" : "text-primary-700"
                }`}
              >
                {state.message}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}